import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, formatMinutes, inboxApi, type SlaPolicy } from '@wa/shared';

/**
 * Response targets — the SLA.
 *
 * Two targets, one switch, one choice of clock. Each target is a deadline stamped
 * on a conversation the moment the customer's first message lands; "breached" is
 * that deadline passing, which is what the inbox's SLA breached view and the badge
 * on the home screen count. Saving re-stamps every open conversation, so a new
 * target is in force at once, not only for the next message.
 *
 * The clock choice is the part that has to be explained, because both readings are
 * true: a message at 21:00 answered at 10:05 is a five-minute reply by the team and
 * a thirteen-hour wait for the customer. Working hours is the number the team can be
 * held to; the clock is what the customer felt.
 */
export function TargetsPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const [draft, setDraft] = useState<SlaPolicy>(EMPTY);

  const sla = useQuery({ queryKey: ['sla'], queryFn: inboxApi.sla });

  useEffect(() => {
    if (sla.data) {
      const { firstResponseMinutes, resolutionMinutes, primaryClock, isActive } = sla.data;
      setDraft({ firstResponseMinutes, resolutionMinutes, primaryClock, isActive });
    }
  }, [sla.data]);

  const save = useMutation({
    mutationFn: () => inboxApi.saveSla(draft),
    onSuccess: ({ recomputed }) => {
      setError(null);
      setSaved(recomputed);
      void queryClient.invalidateQueries({ queryKey: ['sla'] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['counts'] });
    },
    onError: (e) => { setSaved(null); setError(e instanceof ApiError ? e.message : 'Could not save the targets.'); },
  });

  const set = (patch: Partial<SlaPolicy>) => { setSaved(null); setDraft((d) => ({ ...d, ...patch })); };

  const noTarget = draft.firstResponseMinutes === null && draft.resolutionMinutes === null;
  const dirty = sla.data !== undefined && (
    draft.isActive !== sla.data.isActive || draft.primaryClock !== sla.data.primaryClock
    || draft.firstResponseMinutes !== sla.data.firstResponseMinutes || draft.resolutionMinutes !== sla.data.resolutionMinutes
  );
  const hoursMissing = draft.primaryClock === 0 && sla.data !== undefined && !sla.data.hoursEnabled;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">{error}</p>
      )}
      {saved !== null && !save.isPending && (
        <p role="status" className="rounded-(--radius-field) border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
          Saved. {saved === 0 ? 'No open conversations to re-measure.' : `${saved} open ${saved === 1 ? 'conversation' : 'conversations'} re-measured against the new targets.`}
        </p>
      )}

      {/* Status: what the rule is doing right now. The only proof it is live. */}
      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-3 py-3 sm:px-4">
          <div className="min-w-0 flex-1">
            <p className="text-body font-semibold">
              {sla.isPending ? 'Loading…' : sla.data?.isActive ? 'Measuring' : 'Not measuring'}
            </p>
            <p className="text-small text-(--color-muted)">
              {sla.data?.isActive
                ? `${sla.data.measured} open ${sla.data.measured === 1 ? 'conversation carries' : 'conversations carry'} a deadline · ${sla.data.breached} over target right now`
                : 'Response and resolution times are still recorded for reports. Switch on to also count the ones that miss a target.'}
            </p>
          </div>
          {sla.data?.isActive && (
            <Link to="/chats?view=sla" className="tap shrink-0 rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)">
              Open the breached view
            </Link>
          )}
        </div>
      </section>

      <form className="rounded-lg border border-(--color-line) bg-(--color-surface)" onSubmit={(e) => { e.preventDefault(); if (!save.isPending && !(draft.isActive && noTarget)) save.mutate(); }}>
        <div className="flex items-center justify-between gap-3 border-b border-(--color-line) px-3 py-2.5 sm:px-4">
          <h2 className="text-body font-semibold">Targets</h2>
          <Switch on={draft.isActive} onChange={(on) => set({ isActive: on })} label="Measure against targets" />
        </div>

        <div className="flex flex-col divide-y divide-(--color-line-dim)">
          <TargetRow
            label="First reply"
            hint="From the customer's first message to the first reply by a person. A bot's answer does not stop this clock."
            value={draft.firstResponseMinutes}
            onChange={(v) => set({ firstResponseMinutes: v })}
          />
          <TargetRow
            label="Resolved"
            hint="From the customer's first message to the conversation being closed. A conversation that reopens gets a fresh deadline from the message that woke it."
            value={draft.resolutionMinutes}
            onChange={(v) => set({ resolutionMinutes: v })}
          />

          <fieldset className="px-3 py-3 sm:px-4">
            <legend className="text-small font-medium">Count time by</legend>
            <div className="mt-2 grid gap-2 @min-[32rem]:grid-cols-2">
              <ClockChoice
                checked={draft.primaryClock === 0} onPick={() => set({ primaryClock: 0 })}
                title="Working hours"
                body="The clock stops when you are closed. A message at 9 at night with a 15-minute target is due at 10:15 the next morning. The number your team can be held to."
              />
              <ClockChoice
                checked={draft.primaryClock === 1} onPick={() => set({ primaryClock: 1 })}
                title="Around the clock"
                body="The clock never stops. The same message is due at 9:15 that night. What the customer experienced — it counts your opening hours as much as your people."
              />
            </div>
            {hoursMissing && (
              <p className="mt-2 rounded-(--radius-field) bg-(--color-note) px-3 py-2 text-small text-(--color-note-strong)">
                Working hours are off or have no days set, so these targets will run around the clock until they are.{' '}
                <Link to="/settings/hours" className="font-medium underline">Set working hours</Link>
              </p>
            )}
          </fieldset>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-(--color-line) px-3 py-3 sm:px-4">
          <button
            type="submit"
            disabled={save.isPending || !dirty || (draft.isActive && noTarget)}
            className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save targets'}
          </button>
          {draft.isActive && noTarget && (
            <p className="text-small text-(--color-muted)">Set at least one target, or there is nothing to measure against.</p>
          )}
        </div>
      </form>

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-3 text-small text-(--color-muted) sm:px-4">
        <p className="font-medium text-(--color-ink)">What counts as a breach</p>
        <p className="mt-1">
          A conversation nobody has replied to past its first-reply deadline; one that was replied to, but late,
          while it is still open; and one still open past its resolution deadline. Breached conversations appear in
          the inbox under <b>SLA breached</b>, on the home screen, and as a countdown on each row. Closing a
          conversation ends its measurement; the times it earned stay in the reports.
        </p>
      </section>
    </div>
  );
}

const EMPTY: SlaPolicy = { firstResponseMinutes: null, resolutionMinutes: null, primaryClock: 0, isActive: false };

type Unit = 'minutes' | 'hours' | 'days';
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 };

/** The largest unit that divides the value exactly, so 1440 shows as "1 day", 90 as "90 minutes". */
function unitFor(minutes: number | null): Unit {
  if (minutes === null) return 'minutes';
  if (minutes % 1440 === 0) return 'days';
  if (minutes % 60 === 0) return 'hours';
  return 'minutes';
}

function TargetRow({ label, hint, value, onChange }: { label: string; hint: string; value: number | null; onChange: (minutes: number | null) => void }) {
  const [unit, setUnit] = useState<Unit>(() => unitFor(value));
  // A value arriving from the server picks its own unit once; typing keeps the chosen one.
  const [seen, setSeen] = useState(value);
  if (value !== seen) { setSeen(value); if (value !== null && value % UNIT_MINUTES[unit] !== 0) setUnit(unitFor(value)); }

  const shown = value === null ? '' : String(value / UNIT_MINUTES[unit]);
  const on = value !== null;

  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 px-3 py-3 sm:px-4">
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-small font-medium">{label} within</p>
        <p className="text-micro text-(--color-muted)">{hint}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number" min={1} step={1} inputMode="numeric"
          value={shown}
          placeholder="—"
          aria-label={`${label} target`}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(e.target.value === '' || !Number.isFinite(n) || n <= 0 ? null : Math.round(n) * UNIT_MINUTES[unit]);
          }}
          className="w-20 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small tabular-nums outline-none focus:border-(--color-brand)"
        />
        <select
          value={unit}
          aria-label={`${label} unit`}
          onChange={(e) => {
            const next = e.target.value as Unit;
            setUnit(next);
            if (value !== null) onChange(Math.max(1, Math.round(value / UNIT_MINUTES[unit])) * UNIT_MINUTES[next]);
          }}
          className="rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-2 text-small outline-none focus:border-(--color-brand)"
        >
          <option value="minutes">minutes</option>
          <option value="hours">hours</option>
          <option value="days">days</option>
        </select>
        {on ? (
          <button type="button" onClick={() => onChange(null)} className="tap text-small text-(--color-muted) hover:text-(--color-ink)">No target</button>
        ) : (
          <span className="text-small text-(--color-faint)">No target</span>
        )}
      </div>
      {on && value !== null && value % UNIT_MINUTES[unit] !== 0 && (
        <p className="basis-full text-micro text-(--color-muted)">= {formatMinutes(value)}</p>
      )}
    </div>
  );
}

function ClockChoice({ checked, onPick, title, body }: { checked: boolean; onPick: () => void; title: string; body: string }) {
  return (
    <label className={`flex cursor-pointer items-start gap-2.5 rounded-(--radius-field) border px-3 py-2.5 ${checked ? 'border-(--color-brand) bg-(--color-brand-wash)' : 'border-(--color-line) hover:bg-(--color-sunken)'}`}>
      <input type="radio" name="clock" checked={checked} onChange={onPick} className="mt-1 accent-(--color-brand)" />
      <span>
        <span className="block text-small font-medium">{title}</span>
        <span className="block text-micro text-(--color-muted)">{body}</span>
      </span>
    </label>
  );
}

function Switch({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label}
      onClick={() => onChange(!on)}
      className={`tap relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${on ? 'bg-(--color-brand)' : 'bg-(--color-line)'}`}
    >
      <span className={`inline-block size-5 rounded-full bg-white shadow transition ${on ? 'translate-x-5.5' : 'translate-x-0.5'}`} />
    </button>
  );
}
